import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import useWidgetTranslation from '../../../customHooks/useWidgetTranslation';
import { Player, WithTBXPlayerScripts } from '@tbx/experience-widgets-lib';
import { isDesktop } from 'react-device-detect';
import { Redirect } from 'react-router-dom';
import _ from 'lodash';
import * as hooks from './hooks';
import * as customHooks from '../../../customHooks/usePlayerPreferences';
import * as selectors from './selectors';
import useRouter from '../../../customHooks/useRouter';
import { checkifHaveContentAccess } from '../../../utils/validationUserUtils';
import { mapZappingItems } from '../../../utils/zappingUtils';
import './styles.scss';
import { CONTENT_TYPES, TRUCATE_STRING } from '../../../constants/contentTypes';
import { SectionErrorBoundary } from '../../../HOCs/WithErrorBoundary';
import { LIVE_PLAYER, TITLES } from './constants';
import { ERROR_CODE_FORMAT } from '../../../constants/errorCode';
import { usePlayerCallbackState } from '../../App/playerCallbackContextProvider';
import LoadingSpinner from '../../../components/LoadingSpinner';
import {
  validateContentByEnableContentPage,
  validateGoBackOnCompletion,
} from '../../../utils/ContentUtils';
import { startNextEpisode } from './actions';
import { ENVIROMENTS } from '../../../utils/envUtils';
import Seo from '../../../components/Seo';
import { truncateString } from '../../../utils/objectUtils';
import { PATH_ERROR } from '../../AppSectionRouter/constants';
import {
  convertPath,
  getPathNameByContentType,
} from '../../../utils/pathRoutesUtils';
import useBrandInfo from '../../../customHooks/useBrandInfo';
import { useScript } from '../../../customHooks/useScript';
import useClientConfigs from '../../../customHooks/useClientConfigs';
import { useComscoreStreaming } from '../../../customHooks/useComscoreStreaming.js';
import { useSingularContentClick } from '../../../customHooks/useSingularContentClick';
import { useSingularPaywallView } from '../../../customHooks/useSingularPaywallView';
import { hasRedirectToStores } from '@tbx/utils/responsiveWebUtils.js';

function PlayerSection({
  accessToken,
  playerScriptsLoaded,
  playerScriptsError,
  contentId = null,
}) {
  const router = useRouter();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const playerCallbackState = usePlayerCallbackState();
  const translationTexts = {
    ...useWidgetTranslation(TITLES),
    live_player: t(LIVE_PLAYER),
  };
  const contentID = contentId || router.query.contentID;
  // Content list the content was opened from (sent by the carousel)
  const zappingOrigin = router?.location?.state?.zappingOrigin || null;
  // Channel chosen in the zapping carousel: the player changes source with it
  const [zappingContent, setZappingContent] = React.useState(null);
  const playerID = _.uniqueId('player-');
  const PLAYERSECTION_CONTAINER_REF = React.useRef();
  const currentPlayer = window.videojs && window.videojs.getAllPlayers()[0];
  const BRAND_INFO = useBrandInfo();
  const usPrivacy = localStorage.getItem('usPrivacy');
  const { useComscore, useSingular } = useClientConfigs();
  // #region /////// SELECTORS ///////
  const {
    appId,
    packageUpgradeMessages,
    enableContentPage,
    inactivityTimerEnabled,
    inactivityTimer,
    ignoreConcurrencyCalls = false,
    concurrencyTTL = 15,
    concurrencyMaxViews,
  } = useSelector((state) => selectors.selectAppSettings(state));
  const selectedContent = useSelector((state) =>
    selectors.selectSelectedContent(state),
  );
  const isFetching = useSelector((state) => selectors.selectIsFetching(state));
  const authenticated = useSelector((state) =>
    selectors.selectAuthenticated(state),
  );
  const selectError = useSelector((state) => selectors.selectError(state));
  const selectLastEpisodeID = useSelector((state) =>
    selectors.selectLastEpisodeIdInSerie(state),
  );
  const isNextEpisode = useSelector((state) =>
    selectors.selectIsNextEpisode(state),
  );
  const PATHS = useSelector((state) => selectors.selectPathList(state));
  const zappingList = useSelector((state) =>
    selectors.selectZappingList(state),
  );
  const zappingSource = useSelector((state) =>
    selectors.selectZappingSource(state),
  );
  const zappingEpisodes = useSelector((state) =>
    selectors.selectZappingEpisodes(state),
  );
  const showCustomMetaTags = useSelector((state) =>
    selectors.selectShowCustomMetaTags(state),
  );
  // #endregion

  // #region ///// HOOK EFFECTS /////
  hooks.useDidMount(accessToken, contentID);
  hooks.useZappingList(accessToken, zappingOrigin, selectedContent);
  hooks.usePlayerCallbackConfig(
    playerCallbackState,
    contentID,
    selectedContent,
    PATHS,
  );
  const toastAdd = hooks.useToastAdd();
  const notificationModalCreate = hooks.useNotificationModalCreate(
    selectedContent,
    packageUpgradeMessages,
    PATHS,
    accessToken,
    'concurrencyMaxViews',
    concurrencyMaxViews,
  );
  const useLoadWidgetError = hooks.useLoadWidgetError(
    playerScriptsError,
    contentID,
  );
  customHooks.usePlayerPreferencesInit(currentPlayer, false, selectedContent);
  customHooks.usePlayerPreferencesUpdate(currentPlayer);
  useSingularContentClick(selectedContent, useSingular?.events);
  useSingularPaywallView(
    authenticated && selectError?.code === ERROR_CODE_FORMAT.CS011,
    useSingular?.events,
    {
      content_id: selectedContent?.id,
      title: selectedContent?.title,
      content_type: selectedContent?.contentType,
      urn: selectedContent?.urn,
      section_name: 'player',
    },
  );
  useComscore &&
    useComscore.enabled &&
    useComscore.clientId &&
    useComscoreStreaming(
      currentPlayer,
      useComscore.clientId,
      selectedContent,
      useComscore.gender,
    );
  useScript(false);
  // #endregion

  React.useEffect(() => {
    const { CL001, CL002, CP006, CS011, CP015, CP037, CP137, SE003, GE001 } =
      ERROR_CODE_FORMAT;

    const modalCode = [CL001, CL002, CP006, CS011, CP015, CP037, CP137, GE001];

    if (SE003 === selectError?.code) {
      toastAdd({
        code: SE003,
      });
      return router.push(PATHS.home);
    }

    if (modalCode.some((ec) => ec === selectError?.code)) {
      notificationModalCreate(selectError);
      onBack(selectedContent);
    } else if (selectError?.code) {
      toastAdd(selectError);
      onBack(selectedContent);
    }

    if (playerScriptsError) {
      toastAdd(playerScriptsError);
      return router.push(PATHS.home);
    }
  }, [selectError]);

  const onBack = React.useCallback(
    async (content) => {
      if (!PLAYERSECTION_CONTAINER_REF?.current) return;
      PLAYERSECTION_CONTAINER_REF.current.style.visibility = 'hidden';
      PLAYERSECTION_CONTAINER_REF.current.style.opacity = '0';

      if (!content || !content.contentType) {
        return router.push(PATHS.home);
      }

      if (
        [CONTENT_TYPES.BROADCAST, CONTENT_TYPES.SPORT].includes(
          content.contentType,
        )
      ) {
        return router.push(PATHS.home);
      }

      const contentId =
        content.contentType === CONTENT_TYPES.EPISODE
          ? content.serie?.id
          : content.id;

      const enabledContent = validateContentByEnableContentPage(
        enableContentPage,
        content,
      );

      if (
        enabledContent &&
        content.contentType === CONTENT_TYPES.EPISODE &&
        content?.serie?.id
      ) {
        router.push(
          convertPath(
            getPathNameByContentType(CONTENT_TYPES.SERIE, PATHS),
            contentId,
          ),
        );
        return;
      }

      return enabledContent
        ? router.push(PATHS.home)
        : router.push(
            convertPath(
              getPathNameByContentType(content.contentType, PATHS),
              contentId,
            ),
          );
    },
    [router],
  );

  if (isFetching) {
    return (
      <div className="playerSectionContainer playerSectionContainer_loading">
        <LoadingSpinner className="playerSectionContainer_spineer" />
      </div>
    );
  }

  const catchError = (error) => {
    return router.push(PATH_ERROR, { error, navBar: true });
  };

  // The carousel shows, depending on the content type:
  // - BROADCAST: all channels from the source content list, in the order
  //   they're configured. It starts with the items the section already had
  //   loaded (they arrive without waiting for the network) and replaces them
  //   with the service's ones once the list had to be completed.
  // - EPISODE: the remaining episodes of its series' season, requested
  //   directly against the series (doesn't depend on the source content list).
  const isEpisode = selectedContent?.contentType === CONTENT_TYPES.EPISODE;
  const cachedZappingContents =
    !isEpisode && zappingSource?.widgetId === zappingOrigin?.widgetId
      ? zappingSource?.contents || []
      : [];
  const zappingItems = mapZappingItems(
    isEpisode
      ? zappingEpisodes?.contents || []
      : zappingList?.length
        ? zappingList
        : cachedZappingContents,
  );
  const zappingTitle = isEpisode
    ? selectedContent?.serie?.title || ''
    : zappingOrigin?.title;

  const refreshPlayerInChangeSource = (playerId, contentTypeNextEpisode) => {
    if (playerId && contentTypeNextEpisode === CONTENT_TYPES.EPISODE) {
      dispatch(startNextEpisode(true, router, playerId, PATHS.player));
    }
  };

  // EPISODE zapping follows the same logic as the player's native next
  // episode: it navigates to /player/:id instead of doing an in-place
  // changeSrc, so the content is requested again in full (season, episode,
  // series, etc.) and the player ends up with all the data it needs to, in
  // turn, offer the next episode option on the new content.
  //
  // BROADCAST zapping does change source in-place (there's no notion of
  // "next episode" between live channels).
  const onZappingSelect = (item) => {
    const content = item?.content || item;

    if (content?.contentType === CONTENT_TYPES.EPISODE) {
      // The native next episode disposes the player and clears the callback
      // (Player `handleChangeSource`) before navigating. Without that, the new
      // route reuses the old player node and only calls `play()` on it, so the
      // new episode never loads and the spinner stays forever.
      const currentPlayerId = playerCallbackState?.state?.playerID;
      const currentPlayer =
        currentPlayerId && window.videojs?.getPlayer?.(currentPlayerId);

      currentPlayer?.dispose?.();
      playerCallbackState?.clearPlayerCallback?.();

      refreshPlayerInChangeSource(content.id, CONTENT_TYPES.EPISODE);
      return;
    }

    setZappingContent(content);
  };

  const truncatedDescription = truncateString(
    selectedContent?.description,
    TRUCATE_STRING.METATAGS_DESCRIPTION,
  );

  return (
    playerScriptsLoaded && (
      <div className="playerSectionContainer" ref={PLAYERSECTION_CONTAINER_REF}>
        {!showCustomMetaTags && selectedContent && !contentId && (
          <Seo
            title={BRAND_INFO.CLIENT_NAME}
            contentTitle={`${BRAND_INFO.CLIENT_NAME} - ${selectedContent?.title}`}
            description={truncatedDescription}
          />
        )}
        {!selectError?.code &&
        !hasRedirectToStores(isDesktop) &&
        checkifHaveContentAccess(authenticated, selectedContent) ? (
          <Player
            accessToken={accessToken}
            content={selectedContent}
            changeContent={zappingContent}
            zapping={{
              items: zappingItems,
              title: zappingTitle,
              currentContentId: zappingContent?.id || selectedContent?.id,
            }}
            onZappingSelect={onZappingSelect}
            onClickBackButton={() => onBack(selectedContent)}
            playerHandleError={useLoadWidgetError}
            catchError={catchError}
            translationTexts={translationTexts}
            playerCallback={playerCallbackState}
            LoadingComponent={LoadingSpinner}
            refreshPlayerInChangeSource={refreshPlayerInChangeSource}
            isNextEpisode={isNextEpisode}
            enableGoBackOnCompletion={validateGoBackOnCompletion(
              selectedContent,
              selectLastEpisodeID,
            )}
            inactivityTimerEnabled={inactivityTimerEnabled}
            inactivityTimer={inactivityTimer}
            options={{
              castAppId: appId,
              playerID,
              jwtToken: `${accessToken.auth_type} ${accessToken.access_token}`,
              fill: true,
              responsive: true,
              autoplay: true,
              pictureInPictureToggle: true,
              environment: ENVIROMENTS,
              usPrivacy: usPrivacy ?? '',
            }}
            concurrenceLimiter={{
              updateRequest: !ignoreConcurrencyCalls,
              interval: concurrencyTTL,
            }}
          />
        ) : [CONTENT_TYPES.BROADCAST, CONTENT_TYPES.SPORT].includes(
            selectedContent?.contentType,
          ) ? (
          <Redirect to={PATHS.home} />
        ) : (
          <Redirect to={convertPath(PATHS.content, contentID)} />
        )}
      </div>
    )
  );
}

export default SectionErrorBoundary(WithTBXPlayerScripts(PlayerSection));
